/**
 * WorkspaceSystemicPatternsPane — Read-only systemic pattern inspector
 *
 * Surfaces pattern entries with patternRefs, workUnitRefs, continuityRefs,
 * evidenceRefs, severity, and scope classification.
 *
 * Shell B migration v1: migrates 'systemic-patterns' and
 * 'architecture-violation-patterns' panel concepts from UnifiedWorkspace
 * MockGovernancePanels into OperationalPaneModel shape.
 *
 * SAFETY: Read-only, proof-only. No execution authority, no mutation.
 * Pattern visibility does NOT constitute scoring, ranking, or recommendation.
 * No pattern implies a required action. Human review required.
 */

import React from 'react';
import type {
  SystemicPatternsPaneModel,
  SystemicPatternEntry,
  PatternSeverity,
  PatternScope,
} from '../../modules/governanceEventStream/operationalRendering/types.js';
import { NEUTRAL, SPACING, TYPOGRAPHY, SEMANTIC } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface WorkspaceSystemicPatternsPaneProps {
  model: SystemicPatternsPaneModel;
  onSelectEntry?: (entryId: string) => void;
}

function severityColor(severity: PatternSeverity): string {
  if (severity === 'critical') return SEMANTIC.refusal.text;
  if (severity === 'high') return '#e07030';
  if (severity === 'medium') return NEUTRAL.text500;
  return NEUTRAL.text400;
}

function severityBg(severity: PatternSeverity): string {
  if (severity === 'critical') return SEMANTIC.refusal.chip;
  if (severity === 'high') return 'rgba(224,112,48,0.12)';
  return NEUTRAL.bg600;
}

function scopeLabel(scope: PatternScope): string {
  if (scope === 'systemic') return 'SYSTEMIC';
  if (scope === 'local') return 'LOCAL';
  return 'MIXED';
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div style={{
      fontSize: TYPOGRAPHY.micro.size,
      fontWeight: 600,
      color: NEUTRAL.text500,
      textTransform: 'uppercase' as const,
      letterSpacing: 0.4,
      marginBottom: SPACING.sp2,
      marginTop: SPACING.sp3,
    }}>
      {title}
    </div>
  );
}

function PatternRow({ entry, onSelect }: { entry: SystemicPatternEntry; onSelect?: (id: string) => void }) {
  return (
    <div
      onClick={() => onSelect?.(entry.entryId)}
      style={{
        padding: `${SPACING.sp2}px`,
        backgroundColor: entry.isSelected ? NEUTRAL.bg500 : NEUTRAL.bg700,
        borderRadius: 2,
        borderLeft: `2px solid ${entry.isSelected ? severityColor(entry.severity) : 'transparent'}`,
        cursor: onSelect ? 'pointer' : 'default',
        marginBottom: SPACING.sp2,
      }}
      data-testid={`pattern-entry-${entry.entryId}`}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sp2, marginBottom: SPACING.sp1 }}>
        <span style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 700,
          color: severityColor(entry.severity),
          backgroundColor: severityBg(entry.severity),
          padding: '1px 5px',
          borderRadius: 2,
          textTransform: 'uppercase' as const,
          letterSpacing: 0.3,
          flexShrink: 0,
        }}>
          {entry.severity}
        </span>
        <span style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text300,
          backgroundColor: NEUTRAL.bg500,
          padding: '1px 5px',
          borderRadius: 2,
          textTransform: 'uppercase' as const,
          letterSpacing: 0.3,
          flexShrink: 0,
        }}>
          {scopeLabel(entry.scope)}
        </span>
        <span style={{
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text500,
          fontWeight: 600,
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {entry.patternType}
        </span>
        <span style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontFamily: TYPOGRAPHY.mono.family,
          color: NEUTRAL.text300,
          flexShrink: 0,
        }}>
          {entry.timestamp.slice(0, 10)}
        </span>
      </div>

      {/* Description */}
      <div style={{
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text400,
        marginBottom: SPACING.sp1,
        lineHeight: 1.4,
      }}>
        {entry.description}
      </div>

      {/* Linked refs */}
      <div style={{ display: 'flex', gap: SPACING.sp3, flexWrap: 'wrap' }}>
        {entry.workUnitRefs.length > 0 && (
          <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
            {entry.workUnitRefs.length} work unit{entry.workUnitRefs.length !== 1 ? 's' : ''}
          </span>
        )}
        {entry.continuityRefs.length > 0 && (
          <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
            {entry.continuityRefs.length} continuity ref{entry.continuityRefs.length !== 1 ? 's' : ''}
          </span>
        )}
        {entry.evidenceRefs.length > 0 && (
          <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
            {entry.evidenceRefs.length} evidence ref{entry.evidenceRefs.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
}

export const WorkspaceSystemicPatternsPane: React.FC<WorkspaceSystemicPatternsPaneProps> = ({ model, onSelectEntry }) => {
  if (model.error) {
    return (
      <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, fontSize: TYPOGRAPHY.body.size }}>
        Systemic patterns pane error: {model.error}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        backgroundColor: NEUTRAL.bg600,
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: TYPOGRAPHY.heading.size,
          fontWeight: TYPOGRAPHY.heading.weight,
          color: NEUTRAL.text600,
        }}>
          Systemic Patterns
        </span>
        <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
          {model.systemicCount} systemic · {model.localCount} local
          {model.criticalCount > 0 && (
            <span style={{ color: SEMANTIC.refusal.text, marginLeft: 4 }}>
              · {model.criticalCount} critical
            </span>
          )}
        </span>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: SPACING.sp3 }}>

        {/* Safety strip */}
        <div style={{
          display: 'flex',
          gap: SPACING.sp2,
          flexWrap: 'wrap',
          marginBottom: SPACING.sp3,
          paddingBottom: SPACING.sp2,
          borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
        }}>
          {['READ-ONLY', 'PROOF-ONLY', 'NO SCORING OR RANKING', 'NO RECOMMENDATION'].map(label => (
            <span key={label} style={{
              fontSize: TYPOGRAPHY.micro.size,
              fontWeight: 600,
              color: SEMANTIC.stabilized.text,
              backgroundColor: SEMANTIC.stabilized.chip,
              padding: '2px 6px',
              borderRadius: 3,
              textTransform: 'uppercase' as const,
              letterSpacing: 0.3,
            }}>
              {label}
            </span>
          ))}
        </div>

        {/* Critical warning */}
        {model.criticalCount > 0 && (
          <div style={{
            padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
            backgroundColor: SEMANTIC.refusal.chip,
            border: `1px solid ${SEMANTIC.refusal.border}`,
            borderRadius: 2,
            marginBottom: SPACING.sp3,
            fontSize: TYPOGRAPHY.micro.size,
            color: SEMANTIC.refusal.text,
            fontWeight: 600,
          }}>
            {model.criticalCount} critical pattern(s) detected. Human review required.
          </div>
        )}

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: SPACING.sp2,
          marginBottom: SPACING.sp3,
        }}>
          {[
            { label: 'Total', value: model.totalEntries, color: NEUTRAL.text500 },
            { label: 'Systemic', value: model.systemicCount, color: model.systemicCount > 0 ? '#e07030' : NEUTRAL.text400 },
            { label: 'Local', value: model.localCount, color: NEUTRAL.text400 },
            { label: 'Critical', value: model.criticalCount, color: model.criticalCount > 0 ? SEMANTIC.refusal.text : NEUTRAL.text400 },
          ].map(stat => (
            <div key={stat.label} style={{
              backgroundColor: NEUTRAL.bg600,
              border: `1px solid ${NEUTRAL.borderSubtle}`,
              borderRadius: 2,
              padding: `${SPACING.sp2}px`,
              textAlign: 'center' as const,
            }}>
              <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text400, marginBottom: 2 }}>{stat.label}</div>
              <div style={{ fontSize: TYPOGRAPHY.label.size, fontWeight: 600, color: stat.color }}>{stat.value}</div>
            </div>
          ))}
        </div>

        {/* Pattern list */}
        <SectionHeader title={`Patterns (${model.entries.length})`} />
        {model.entries.length === 0 ? (
          <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, textAlign: 'center' as const }}>
            No patterns detected
          </div>
        ) : (
          <div>
            {model.entries.map(entry => (
              <PatternRow key={entry.entryId} entry={entry} onSelect={onSelectEntry} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
};
